import React from 'react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
);


const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            labels: {
                color: 'rgb(156, 163, 175)', // gray-400
                font: { size: 12 }
            }
        },
        tooltip: {
            backgroundColor: 'rgba(31, 41, 55, 0.9)', // gray-800
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: 'rgb(75, 192, 192)',
            borderWidth: 1,
        }
    },
    scales: {
        x: {
            ticks: { color: 'rgb(156, 163, 175)' },
            grid: { color: 'rgba(156, 163, 175, 0.1)' }
        },
        y: {
            ticks: { color: 'rgb(156, 163, 175)' },
            grid: { color: 'rgba(156, 163, 175, 0.1)' }
        }
    }
};


export const ActivityLineChart = ({ data, title }) => {
    const chartData = {
        labels: data.map(d => d.month), // MM/YYYY
        datasets: [{
            label: 'Uploads',
            data: data.map(d => d.count),
            borderColor: 'rgb(96, 165, 250)', // blue-400
            backgroundColor: 'rgba(96, 165, 250, 0.5)',
            tension: 0.4,
            fill: true,
        }],
    };

    const options = {
        ...chartOptions,
        scales: {
            ...chartOptions.scales,
            y: {
                ...chartOptions.scales.y,
                min: 0,
                beginAtZero: true,
                title: { display: true, text: 'Nº de Exames', color: 'rgb(156, 163, 175)' }
            }
        },
        plugins: {
            ...chartOptions.plugins,
            title: { display: true, text: title, color: '#fff', font: { size: 16 } }
        }
    };

    return <Line data={chartData} options={options} />;
};


export const DonutChart = ({ title, data, totalLabel }) => {
    const COLORS = {
        Sucesso: 'rgb(16, 185, 129)', // green-500
        Processando: 'rgb(250, 204, 21)', // yellow-400
        Erro: 'rgb(239, 68, 68)', // red-500
        Uploaded: 'rgb(99, 102, 241)', // indigo-500
        Viewed: 'rgb(59, 130, 246)', // blue-500
        Edited: 'rgb(16, 185, 129)', // green-500
        Approved: 'rgb(236, 72, 153)', // pink-500
    };

    const total = data.reduce((sum, item) => sum + item.count, 0);

    const chartData = {
        labels: data.map(d => d.label),
        datasets: [{
            data: data.map(d => d.count),
            backgroundColor: data.map(d => COLORS[d.label] || 'rgb(107, 114, 128)'), // gray-500 fallback
            borderColor: 'rgb(31, 41, 55)', // gray-800
            borderWidth: 2,
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '68%',
        layout: {
            padding: {
                top: 4,
                bottom: 4,
            },
        },
        plugins: {
            ...chartOptions.plugins,
            title: {
                display: true,
                text: title,
                color: '#fff',
                font: { size: 16 },
                padding: { top: 0, bottom: 8 },
            },
            legend: {
                ...chartOptions.plugins.legend,
                position: 'bottom',
                labels: {
                    ...chartOptions.plugins.legend.labels,
                    boxWidth: 10,
                    padding: 8,
                    font: { size: 11 },
                },
            },
            tooltip: {
                ...chartOptions.plugins.tooltip,
                callbacks: {
                    label: ({ label, raw }) => `${label}: ${raw} (${total > 0 ? ((raw / total) * 100).toFixed(1) : 0}%)`,
                },
            },
        },
    };

    const plugins = [
        {
            id: 'doughnutTotal',
            afterDraw: function (chart) {
                const { ctx, chartArea } = chart;
                if (!chartArea) return;

                const meta = chart.getDatasetMeta(0);
                const arc = meta?.data?.[0];
                const centerX = arc ? arc.x : (chartArea.left + chartArea.right) / 2;
                const centerY = arc ? arc.y : (chartArea.top + chartArea.bottom) / 2;
                const innerRadius = arc?.innerRadius || (Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) / 2) * 0.68;

                const valueFontSize = Math.max(16, Math.min(30, Math.round(innerRadius * 0.45)));
                const labelFontSize = Math.max(10, Math.min(13, Math.round(innerRadius * 0.22)));

                ctx.save();
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                // Valor total no centro do anel
                ctx.font = `bold ${valueFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
                ctx.fillStyle = '#ffffff';
                ctx.fillText(total.toString(), centerX, centerY - labelFontSize * 0.6);

                // Label (Total)
                ctx.font = `500 ${labelFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
                ctx.fillStyle = 'rgb(156, 163, 175)';
                const labelText = totalLabel || 'Total';
                ctx.fillText(labelText, centerX, centerY + valueFontSize * 0.6);

                ctx.restore();
            },
        },
    ];

    return (
        <div className="relative w-full h-full min-h-[250px] flex items-center justify-center">
            <Doughnut data={chartData} options={options} plugins={plugins} />
        </div>
    );
};


export const ExamFunnelChart = ({ data }) => {
    const labels = data.map(d => d.label);
    const counts = data.map(d => d.count);
    const initialCount = counts[0] || 1;

    const chartData = {
        labels: labels,
        datasets: [{
            label: 'Nº de Exames',
            data: counts,
            backgroundColor: ['rgb(99, 102, 241)', 'rgb(236, 72, 13)', 'rgb(59, 130, 246)', 'rgb(16, 185, 129)', 'rgb(10, 10, 190)', 'rgb(16, 200, 0)'],
            borderColor: 'rgb(31, 41, 55)',
            borderWidth: 1,
        }],
    };

    const options = {
        ...chartOptions,
        indexAxis: 'y',
        scales: {
            x: {
                ...chartOptions.scales.x,
                beginAtZero: true,
                title: { display: true, text: 'Contagem de Exames', color: 'rgb(156, 163, 175)' }
            },
            y: {
                ...chartOptions.scales.y,
                stacked: true,
                grid: { display: false }
            }
        },
        plugins: {
            ...chartOptions.plugins,
            title: { display: true, text: 'Funil de Fluxo de Trabalho de Exames', color: '#fff', font: { size: 16 } },
            legend: { display: false },
            tooltip: {
                ...chartOptions.plugins.tooltip,
                callbacks: {
                    label: ({ label, raw }) => `${label}: ${raw} (${((raw / initialCount) * 100).toFixed(1)}% do Upload)`,
                    title: (context) => context[0].label
                }
            }
        }
    };

    return <Bar data={chartData} options={options} />;
};


export const GrowthLineChart = ({ data, title }) => {
    const chartData = {
        labels: data.map(d => d.month), // MM/YYYY
        datasets: [{
            label: 'Total Acumulado',
            data: data.map(d => d.count),
            borderColor: 'rgb(167, 243, 208)', // green-200
            backgroundColor: 'rgba(167, 243, 208, 0.5)',
            tension: 0.4,
            fill: true,
        }],
    };

    const options = {
        ...chartOptions,
        scales: {
            ...chartOptions.scales,
            y: {
                ...chartOptions.scales.y,
                min: 0,
                beginAtZero: true,
                title: { display: true, text: 'Nº Acumulado', color: 'rgb(156, 163, 175)' }
            }
        },
        plugins: {
            ...chartOptions.plugins,
            title: { display: true, text: title, color: '#fff', font: { size: 16 } }
        }
    };

    return <Line data={chartData} options={options} />;
};


export const ServiceBarChart = ({ data }) => {
    const labels = Object.keys(data);
    const counts = Object.values(data);

    const chartData = {
        labels: labels,
        datasets: [{
            label: 'Usuários Ativos (Com Acesso)',
            data: counts,
            backgroundColor: 'rgb(59, 130, 246)', // blue-500
            borderColor: 'rgb(31, 41, 55)',
            borderWidth: 1,
        }],
    };

    const options = {
        ...chartOptions,
        scales: {
            x: {
                ...chartOptions.scales.x,
                title: { display: true, text: 'Serviço', color: 'rgb(156, 163, 175)' }
            },
            y: {
                ...chartOptions.scales.y,
                min: 0,
                beginAtZero: true,
                title: { display: true, text: 'Nº de Usuários', color: 'rgb(156, 163, 175)' }
            }
        },
        plugins: {
            ...chartOptions.plugins,
            title: { display: true, text: 'Adoção de Serviços por Usuário', color: '#fff', font: { size: 16 } },
            legend: { display: false },
        }
    };

    return <Bar data={chartData} options={options} />;
};
